// ── Gyroscope Aiming ──
// Uses DeviceMotionEvent rotationRate for fine aim adjustments on mobile.
// MOB-L3: optionally adds accelerationIncludingGravity as a tilt-based
// fine-aim signal (off by default).

const DEFAULT_SENSITIVITY = 0.015;
const DEAD_ZONE_DEG_SEC = 1.5;
/** MOB-L3: m/s^2 magnitude below this is treated as noise/hand tremor. */
const ACCEL_DEAD_ZONE_MS2 = 0.5;
/** MOB-L3: fine-aim weight applied to accelerometer tilt (small — accelerometer
 *  is supplementary to rotationRate, not a replacement). */
const ACCEL_WEIGHT = 0.25;

/**
 * F-CM-012: read the current screen orientation angle, preferring the
 * standard `screen.orientation.angle` and falling back to legacy
 * `window.orientation` on older Safari builds. Returns 0 (portrait-primary)
 * if no orientation API is available.
 */
function readOrientationAngle(): number {
  if (typeof screen !== 'undefined' && screen.orientation && typeof screen.orientation.angle === 'number') {
    return screen.orientation.angle;
  }
  const legacy = (window as unknown as { orientation?: number }).orientation;
  if (typeof legacy === 'number') return legacy;
  return 0;
}

export class GyroAimController {
  private dx = 0;
  private dy = 0;
  private enabled = false;

  /** Sensitivity multiplier for gyro input. */
  sensitivity: number;

  /** Dead zone threshold in degrees/sec. F-MOB-H1: now mutable so settings UI
   *  can adjust it without re-instantiating the controller. */
  private deadZone: number;

  /** MOB-L3: optional accelerometer-based fine aim (off by default). */
  private useAccelerometer = false;

  // Bound handler
  private readonly handleMotion: (e: DeviceMotionEvent) => void;

  /** Visibility change handler to reset accumulated delta on tab switch. */
  private readonly _onVisibility: () => void;

  /** G-M-004: tracks whether _onVisibility is currently registered on document.
   *  Mirrors the `enabled` flag so dispose() only removes a listener that was
   *  actually added. */
  private visibilityAttached = false;

  constructor(sensitivity = DEFAULT_SENSITIVITY, deadZone = DEAD_ZONE_DEG_SEC) {
    this.sensitivity = sensitivity;
    this.deadZone = deadZone;

    this.handleMotion = (e: DeviceMotionEvent) => {
      if (!this.enabled) return;
      const rate = e.rotationRate;
      if (!rate) return;

      // F-CM-012: DeviceMotionEvent rotationRate is reported in the device
      // frame (X = short edge, Y = long edge). When the phone is held in
      // landscape — the canonical FPS orientation — beta and gamma swap
      // roles, with sign flips between landscape-primary and -secondary.
      // Read the current screen orientation and remap so the player's
      // intended yaw/pitch axes line up regardless of how they're holding
      // the phone.
      const beta = rate.beta ?? 0;
      const gamma = rate.gamma ?? 0;
      const orientationAngle = readOrientationAngle();
      let yawRate: number;
      let pitchRate: number;
      switch (orientationAngle) {
        case 90:
          // landscape-primary: phone rotated so the home button is on the
          // right. beta becomes yaw (sign flipped), gamma becomes pitch.
          yawRate = -beta;
          pitchRate = gamma;
          break;
        case -90:
        case 270:
          // landscape-secondary: home button on the left. Both axes flip
          // relative to landscape-primary.
          yawRate = beta;
          pitchRate = -gamma;
          break;
        case 180:
          // upside-down portrait — rare on phones but supported.
          yawRate = -gamma;
          pitchRate = -beta;
          break;
        case 0:
        default:
          // portrait-primary: original mapping.
          yawRate = gamma;
          pitchRate = beta;
          break;
      }

      // Apply dead zone in the remapped space so the threshold tracks the
      // player's actual yaw/pitch motion rather than raw beta/gamma.
      const yaw = Math.abs(yawRate) > this.deadZone ? yawRate : 0;
      const pitch = Math.abs(pitchRate) > this.deadZone ? pitchRate : 0;

      // Convert degrees/sec to delta. Interval is in ms, convert to sec.
      const dt = (e.interval ?? 16) / 1000;
      this.dx += yaw * dt * this.sensitivity;
      this.dy += pitch * dt * this.sensitivity;

      // MOB-L3: optional secondary signal from accelerationIncludingGravity.
      // Treats lateral tilt (gravity X component) as yaw and longitudinal tilt
      // (gravity Y component minus 9.81 baseline) as pitch. Weighted small so
      // it nudges aim instead of overriding rotationRate.
      if (this.useAccelerometer) {
        const accel = e.accelerationIncludingGravity;
        if (accel) {
          const ax = accel.x ?? 0;
          // y is dominated by gravity (~9.81 m/s^2 when phone is held upright);
          // subtract that baseline so neutral tilt produces ~0 contribution.
          const ay = (accel.y ?? 0) - 9.81;
          // BUG-MT-22: accelerationIncludingGravity is reported in the device
          // frame, same as rotationRate. In landscape the short edge (X) and
          // long edge (Y) swap their meaning for yaw/pitch, with sign flips
          // between landscape-primary and -secondary. Mirror the same
          // orientation remapping the rotationRate branch applies so
          // accelerometer-driven fine aim is correct in every hold posture.
          let tiltYaw: number;
          let tiltPitch: number;
          switch (orientationAngle) {
            case 90:
              // landscape-primary: home button on the right. ax (short edge)
              // becomes yaw with sign flipped; ay (long edge minus gravity)
              // becomes pitch.
              tiltYaw = -ax;
              tiltPitch = ay;
              break;
            case -90:
            case 270:
              // landscape-secondary: home button on the left. Both axes flip
              // relative to landscape-primary.
              tiltYaw = ax;
              tiltPitch = -ay;
              break;
            case 180:
              // upside-down portrait — rare on phones but supported.
              tiltYaw = -ay;
              tiltPitch = -ax;
              break;
            case 0:
            default:
              // portrait-primary: original mapping (lateral tilt → yaw,
              // longitudinal tilt → pitch).
              tiltYaw = ax;
              tiltPitch = ay;
              break;
          }
          const tiltX = Math.abs(tiltYaw) > ACCEL_DEAD_ZONE_MS2 ? tiltYaw : 0;
          const tiltY = Math.abs(tiltPitch) > ACCEL_DEAD_ZONE_MS2 ? tiltPitch : 0;
          this.dx += tiltX * dt * this.sensitivity * ACCEL_WEIGHT;
          this.dy += tiltY * dt * this.sensitivity * ACCEL_WEIGHT;
        }
      }
    };

    this._onVisibility = () => {
      if (document.hidden) { this.dx = 0; this.dy = 0; }
    };
    // G-M-004: previously this constructor unconditionally registered a
    // visibilitychange listener. Every short-lived GyroAimController instance
    // (including ones created on desktop where gyro is never started) leaked
    // the listener until dispose() ran. The registration now happens inside
    // startListening() and mirrors the controller's enabled state.
  }

  /**
   * Request gyroscope permission (required on iOS 13+).
   * Must be called from a user gesture handler.
   * Returns true if permission was granted or not needed.
   */
  async requestPermission(): Promise<boolean> {
    const dme = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<string>;
    };
    if (typeof dme.requestPermission === 'function') {
      try {
        const result = await dme.requestPermission();
        if (result === 'granted') {
          this.startListening();
          return true;
        }
        return false;
      } catch {
        return false;
      }
    }

    // No permission needed — just start listening
    this.startListening();
    return true;
  }

  private startListening(): void {
    if (this.enabled) return;
    this.enabled = true;
    window.addEventListener('devicemotion', this.handleMotion);
    // G-M-004: attach the visibilitychange listener only when we actually
    // begin consuming device motion events. Idempotent — we never double
    // register because `enabled` short-circuits above.
    if (!this.visibilityAttached) {
      document.addEventListener('visibilitychange', this._onVisibility);
      this.visibilityAttached = true;
    }
  }

  /** MOB-L3: enable/disable accelerometer-based fine aim. */
  setUseAccelerometer(enabled: boolean): void {
    this.useAccelerometer = !!enabled;
  }

  /** F-MOB-H1: set the gyro rotationRate dead zone (degrees/sec). */
  setDeadZone(value: number): void {
    if (!Number.isFinite(value)) return;
    this.deadZone = Math.max(0, Math.min(10, value));
  }

  /** F-MOB-H1: read the current dead zone. */
  getDeadZone(): number {
    return this.deadZone;
  }

  /** MOB-L3: read accelerometer enabled state. */
  isAccelerometerEnabled(): boolean {
    return this.useAccelerometer;
  }

  /** Get accumulated gyro aim delta since last getDelta/reset. */
  getDelta(): { dx: number; dy: number } {
    const result = { dx: this.dx, dy: this.dy };
    this.dx = 0;
    this.dy = 0;
    return result;
  }

  /** Reset the current orientation as center (zero out accumulated delta). */
  calibrate(): void {
    this.dx = 0;
    this.dy = 0;
  }

  /** Stop listening and clean up. Only removes listener if it was registered. */
  dispose(): void {
    if (this.enabled) {
      window.removeEventListener('devicemotion', this.handleMotion);
    }
    // G-M-004: only remove the visibilitychange listener if startListening
    // actually attached it. Calling removeEventListener for a never-attached
    // listener is harmless in standard browsers, but the symmetry makes the
    // attach/detach lifecycle explicit and avoids leaking calls in tests
    // that count addEventListener / removeEventListener invocations.
    if (this.visibilityAttached) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this.visibilityAttached = false;
    }
    this.enabled = false;
    this.dx = 0;
    this.dy = 0;
  }
}
